import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, FileStack, Code2, Zap, Shield, Layers } from 'lucide-react';

export const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Sparkles,
      title: 'AI Document Extraction',
      description: 'Automatically extract structured data from any document using advanced AI models.',
      color: 'from-[#635BFF] to-[#7C3AED]',
      bgColor: 'bg-[#635BFF]/10',
    },
    {
      icon: FileStack,
      title: 'Multiple Document Types',
      description: 'Invoices, receipts, ID cards, purchase orders, and more. One API for all.',
      color: 'from-[#00D4FF] to-[#0EA5E9]',
      bgColor: 'bg-[#00D4FF]/10',
    },
    {
      icon: Code2,
      title: 'Developer Friendly',
      description: 'Clean REST API with SDKs for JavaScript, Python, and more. Easy integration.',
      color: 'from-[#10B981] to-[#059669]',
      bgColor: 'bg-[#10B981]/10',
    },
    {
      icon: Zap,
      title: 'Fast Processing',
      description: 'Documents processed asynchronously with real-time webhooks for completion.',
      color: 'from-[#F59E0B] to-[#D97706]',
      bgColor: 'bg-[#F59E0B]/10',
    },
    {
      icon: Shield,
      title: 'Secure Storage',
      description: 'Enterprise-grade security with encryption at rest and in transit. SOC 2 compliant.',
      color: 'from-[#EF4444] to-[#DC2626]',
      bgColor: 'bg-[#EF4444]/10',
    },
    {
      icon: Layers,
      title: 'Batch Processing',
      description: 'Process hundreds of documents at once with our powerful batch processing engine.',
      color: 'from-[#8B5CF6] to-[#7C3AED]',
      bgColor: 'bg-[#8B5CF6]/10',
    },
  ];

  return (
    <section
      ref={ref}
      id="features"
      className="py-20 md:py-28 bg-white"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#635BFF]/10 text-[#635BFF] text-sm font-medium rounded-full mb-4">
            Features
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6">
            Everything You Need for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#635BFF] to-[#7C3AED]">
              Document Automation
            </span>
          </h2>
          <p className="text-lg text-[#425466] leading-relaxed">
            Powerful features designed for developers and businesses who need reliable, accurate document processing at scale.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#635BFF]/30 hover:shadow-xl transition-all duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div
                className={`w-14 h-14 ${feature.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
              >
                <feature.icon
                  className={`w-7 h-7 bg-gradient-to-br ${feature.color} bg-clip-text`}
                  style={{
                    color: feature.color.includes('635BFF')
                      ? '#635BFF'
                      : feature.color.includes('00D4FF')
                      ? '#00D4FF'
                      : feature.color.includes('10B981')
                      ? '#10B981'
                      : feature.color.includes('F59E0B')
                      ? '#F59E0B'
                      : feature.color.includes('EF4444')
                      ? '#EF4444'
                      : '#8B5CF6',
                  }}
                />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0A2540] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#425466] leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
